import { ref } from 'vue'

// ─────────────────────────────────────────────────────────────────────────────
// useChatAI — composable for streaming chat with Yustian Smart AI (running on
// the Yustian AI Engine on the host).
//
// Backend endpoint: POST /api/chat (SSE: 'session' | 'delta' | 'tool' |
//                                       'tool_result' | 'done' | 'error')
// Sessions are persisted to SQL Server keyed on (userId, department, reportId,
// sessionDate). The composable knows how to:
//   - send a new message in a streaming turn
//   - resume/replace messages from a stored session
//   - track token usage and tool activity
//   - retry the last user message after an error
//
// ARCHITECTURE — multi-tab parallel chat:
//   The whole state is built by `createChatInstance()`, a FACTORY so each open
//   tab owns a fully independent instance (own refs, own AbortController, own
//   fetch loop). Two tabs can therefore stream concurrently — a background tab
//   keeps writing into its own refs while the user reads/asks in another tab.
//   `useChatAI()` returns a single shared default instance for callers that do
//   not need tabs (and keeps the old call-site API working unchanged).
// ─────────────────────────────────────────────────────────────────────────────

// Tail-aware strip: hilangkan blok <follow-ups>… dari teks yang sedang
// di-stream. Menerima `raw` (akumulasi penuh) lalu mengembalikan teks yang
// aman untuk dirender. Menutup juga "kemungkinan-pembuka" parsial di akhir
// (mis. raw berakhir dengan "<f", "<follow-up", dst.) supaya marker yang
// terbelah lintas-delta tidak sempat tampil sebelum komplet.
const FU_OPEN_RE   = /<follow-ups>/i
const FU_TAIL_RE   = /<f[a-z\-/]*$/i
function stripFollowUpsLive(raw) {
  if (!raw) return ''
  const m = raw.match(FU_OPEN_RE)
  if (m) return raw.slice(0, m.index).replace(/\s+$/, '')
  return raw.replace(FU_TAIL_RE, '')
}

// Pola template generik yang DILARANG di system prompt — kalau AI sempat
// kembali ke kalimat seperti ini, drop saja daripada menampilkan saran yang
// tidak kontekstual ke jawaban. Cocokan longgar (case-insensitive, prefix-ish).
const GENERIC_FOLLOWUP_PATTERNS = [
  /^berikan contoh konkret/i,
  /^apa risikonya/i,
  /^buatkan action plan/i,
  /^apa next step/i,
  /^apa langkah selanjutnya/i,
  /^jelaskan lebih (lanjut|detail)\.?$/i,
  /^bisa dijelaskan lebih/i,
  /^apa kesimpulannya\??$/i,
]
function isGenericFollowUp(s) {
  return GENERIC_FOLLOWUP_PATTERNS.some(re => re.test(s))
}

function parseFollowUpsFromRaw(raw) {
  if (!raw) return []
  const m = raw.match(/<follow-ups>\s*([\s\S]*?)\s*<\/follow-ups>/i)
  if (!m) return []
  return m[1]
    .split(/\r?\n/)
    .map(s => s.replace(/^[-*•\d.\s]+/, '').trim())
    .filter(s => s.length > 0 && s.length <= 200)
    .filter(s => !isGenericFollowUp(s))
    .slice(0, 3)
}

function parseSseChunk(buffer) {
  const events = []
  let remaining = buffer
  let boundary = remaining.indexOf('\n\n')
  while (boundary !== -1) {
    const rawEvent = remaining.slice(0, boundary)
    remaining = remaining.slice(boundary + 2)
    let eventName = 'message'
    let dataStr = ''
    for (const line of rawEvent.split('\n')) {
      if (line.startsWith('event:')) eventName = line.slice(6).trim()
      else if (line.startsWith('data:')) dataStr += line.slice(5).trim()
    }
    if (dataStr) {
      try { events.push({ event: eventName, data: JSON.parse(dataStr) }) }
      catch { /* ignore malformed event */ }
    }
    boundary = remaining.indexOf('\n\n')
  }
  return [events, remaining]
}

// Global, instance-independent counter so message IDs never collide across tabs.
let _globalMsgIdCounter = 0
function nextMessageId() {
  _globalMsgIdCounter += 1
  return `msg_${Date.now()}_${_globalMsgIdCounter}`
}

// ── Factory ──────────────────────────────────────────────────────────────────
// Builds one fully self-contained chat instance. Every tab gets its own.
export function createChatInstance() {
  const messages        = ref([]) // [{ id, role, content, streaming?, error?, retryable? }]
  const isStreaming     = ref(false)
  const error           = ref(null)
  const sessionId       = ref(null)
  const sessionTitle    = ref(null)
  const lastUsage       = ref(null)        // { inputTokens, outputTokens, ... }
  const cumulativeUsage = ref({ input: 0, output: 0 })
  const toolActivity    = ref([])          // [{ name, input, ok?, rowCount? }]

  let _abortController = null
  let _lastSendArgs    = null

  function buildApiMessages() {
    return messages.value
      .filter(m => m.content && String(m.content).trim().length > 0 && !m.error)
      .map(m => ({ role: m.role, content: String(m.content) }))
  }

  async function sendMessage(userText, opts = {}) {
    if (!userText || !userText.trim() || isStreaming.value) return

    error.value = null
    toolActivity.value = []

    // Pull images out of opts so we can both attach to the local user message
    // (for inline rendering) and forward to the backend.
    const turnImages = Array.isArray(opts.images) ? opts.images : []
    const userMsg = {
      id: nextMessageId(),
      role: 'user',
      content: userText.trim(),
      createdAt: Date.now(),
      images: turnImages.length
        ? turnImages.map(i => ({ name: i.name, mediaType: i.mediaType, dataUrl: i.dataUrl }))
        : undefined,
    }
    messages.value.push(userMsg)

    messages.value.push({
      id: nextMessageId(),
      role: 'assistant',
      content: '',
      streaming: true,
      createdAt: Date.now(),
    })
    // Use the reactive proxy (not the raw object) so Vue re-renders on each delta.
    const aMsg = messages.value[messages.value.length - 1]

    isStreaming.value = true
    _abortController = new AbortController()
    _lastSendArgs = { userText: userMsg.content, opts }

    // Buffer mentah untuk deteksi blok <follow-ups>. `aMsg.content` selalu
    // berisi versi yang sudah dibersihkan (siap render); marker yang belum
    // komplet juga ditahan agar tidak sempat tampil ke user.
    let rawBuffer = ''

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages:    buildApiMessages(),
          context:     opts.context     || '',
          department:  opts.department  || null,
          reportId:    opts.reportId    || null,
          sessionDate: opts.sessionDate || null,
          sessionId:   sessionId.value  || null,
          token:       opts.token       || null,
          // Images for vision — base64 attached only on the latest user turn.
          // Backend transforms them into image content blocks for Claude.
          images: turnImages.length
            ? turnImages.map(i => ({ name: i.name, mediaType: i.mediaType, base64: i.base64 }))
            : undefined,
        }),
        signal: _abortController.signal,
      })

      if (!response.ok) {
        let errMsg = `HTTP ${response.status}`
        try {
          const errBody = await response.json()
          if (errBody?.message) errMsg = errBody.message
        } catch { /* ignore */ }
        throw new Error(errMsg)
      }
      if (!response.body) throw new Error('Response body is empty')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const [events, remaining] = parseSseChunk(buffer)
        buffer = remaining

        for (const evt of events) {
          if (evt.event === 'session') {
            sessionId.value = evt.data?.sessionId ?? null
            if (evt.data?.title) sessionTitle.value = evt.data.title
          } else if (evt.event === 'delta' && typeof evt.data?.text === 'string') {
            rawBuffer += evt.data.text
            aMsg.content = stripFollowUpsLive(rawBuffer)
          } else if (evt.event === 'tool') {
            toolActivity.value.push({
              id: evt.data?.id ?? null,
              name: evt.data?.name,
              input: evt.data?.input,
              startedAt: Date.now(),
              endedAt: null,
            })
          } else if (evt.event === 'tool_result') {
            // Cocokkan hasil ke tool yang benar. AI bisa memanggil banyak tool
            // paralel dalam 1 giliran, jadi tidak boleh asal pakai item terakhir:
            // utamakan match by id, lalu tool jalan pertama dgn nama sama, lalu
            // item terakhir (fallback kompat server lama tanpa id).
            const arr = toolActivity.value
            const rid = evt.data?.id
            let target = rid != null ? arr.find(t => t.id === rid) : null
            if (!target) target = arr.find(t => !t.endedAt && t.name === evt.data?.name)
            if (!target) target = arr.find(t => !t.endedAt)
            if (!target) target = arr[arr.length - 1]
            if (target) {
              target.ok = evt.data?.ok
              target.rowCount = evt.data?.rowCount
              target.preview = evt.data?.preview ?? null
              target.endedAt = Date.now()
            }
          } else if (evt.event === 'done') {
            aMsg.streaming = false
            // Final cleanup: pastikan content sudah bebas dari marker, lalu
            // ekstrak follow-ups. Server juga mengirim followUps di payload
            // done (sumber utama); fallback ke parser lokal bila absen
            // (mis. server lama atau payload terpotong).
            aMsg.content = stripFollowUpsLive(rawBuffer)
            const serverFollowUps = Array.isArray(evt.data?.followUps) ? evt.data.followUps : null
            const parsed = serverFollowUps && serverFollowUps.length
              ? serverFollowUps
              : parseFollowUpsFromRaw(rawBuffer)
            if (parsed.length) aMsg.followUps = parsed
            if (evt.data?.usage) {
              lastUsage.value = evt.data.usage
              cumulativeUsage.value.input  += evt.data.usage.inputTokens  || 0
              cumulativeUsage.value.output += evt.data.usage.outputTokens || 0
            }
          } else if (evt.event === 'error') {
            throw new Error(evt.data?.message || 'Stream error')
          }
        }
      }
      aMsg.streaming = false
      // Guard: stream closed without delivering content (tool-only turn or connection drop).
      // Prevents an invisible empty bubble — content must be truthy for MarkdownRenderer to render.
      if (!aMsg.content && !aMsg.error) {
        aMsg.error = 'empty_response'
        aMsg.retryable = true
        aMsg.content = '_(respons tidak diterima — ketuk **Ulangi** untuk coba lagi)_'
      }
    } catch (err) {
      aMsg.streaming = false
      if (err.name === 'AbortError') {
        if (!aMsg.content) aMsg.content = '_(dibatalkan)_'
        else aMsg.content += '\n\n_(dibatalkan)_'
      } else {
        aMsg.error = err.message || 'Unknown error'
        aMsg.retryable = true
        error.value = err.message || 'Unknown error'
        if (!aMsg.content) {
          aMsg.content = `⚠️ ${aMsg.error}`
        }
      }
    } finally {
      // Snapshot jejak tool ke pesan assistant (kartu "Sumber data" per-pesan)
      // SEBELUM toolActivity di-reset oleh turn berikutnya — penting agar angka
      // dari akun Google Ads live tetap bisa diaudit setelah jawaban selesai.
      if (aMsg && toolActivity.value.length) {
        aMsg.tools = toolActivity.value.map(t => ({ ...t }))
      }
      isStreaming.value = false
      _abortController = null
    }
  }

  function abortStream() {
    if (_abortController) _abortController.abort()
  }

  function clearMessages() {
    abortStream()
    messages.value = []
    error.value = null
    sessionId.value = null
    sessionTitle.value = null
    lastUsage.value = null
    cumulativeUsage.value = { input: 0, output: 0 }
    toolActivity.value = []
  }

  async function retryLast() {
    if (!_lastSendArgs || isStreaming.value) return
    // Pop the failed assistant + the user message we previously sent
    while (messages.value.length && messages.value[messages.value.length - 1].role !== 'user') {
      messages.value.pop()
    }
    if (messages.value.length && messages.value[messages.value.length - 1].role === 'user') {
      messages.value.pop()
    }
    // Reset tool activity so the timeline doesn't show stale entries from the failed turn.
    toolActivity.value = []
    await sendMessage(_lastSendArgs.userText, _lastSendArgs.opts)
  }

  function loadSession(serverSession) {
    // serverSession: { sessionId, title, messages: [...] }
    abortStream()
    sessionId.value = serverSession?.sessionId ?? null
    sessionTitle.value = serverSession?.title ?? null
    toolActivity.value = []
    lastUsage.value = null
    cumulativeUsage.value = { input: 0, output: 0 }
    messages.value = (serverSession?.messages || [])
      // Skip messages with empty content — they represent incomplete turns not
      // saved to DB (tool-only turns, aborted streams) that should not surface as blank bubbles.
      .filter(m => m.content && String(m.content).trim().length > 0)
      .map(m => ({
        id: nextMessageId(),
        messageId: m.messageId ?? null,
        role: m.role === 'tool' ? 'assistant' : m.role,
        content: m.content != null ? String(m.content) : '',
        createdAt: m.createdAt ? new Date(m.createdAt).getTime() : Date.now(),
      }))
  }

  function setSession(id, title) {
    sessionId.value = id ?? null
    sessionTitle.value = title ?? null
  }

  return {
    messages,
    isStreaming,
    error,
    sessionId,
    sessionTitle,
    lastUsage,
    cumulativeUsage,
    toolActivity,
    sendMessage,
    abortStream,
    clearMessages,
    retryLast,
    loadSession,
    setSession,
  }
}

// Shared default instance — backward-compatible singleton for callers that do
// not opt into multi-tab. All existing call sites keep working unchanged.
let _defaultInstance = null
export function useChatAI() {
  if (!_defaultInstance) _defaultInstance = createChatInstance()
  return _defaultInstance
}
